import type { UseEditorOptions } from "@tiptap/react";
import type { ForwardedRef } from "react";

import React, { useEffect } from "react";

import type { TooltipProps } from "@/components/tooltip";

import { Description } from "@/components/field";

import { EditorContainer } from "./EditorContainer";
import { EditorLabel } from "./EditorLabel";
import { useInitializeEditor } from "./useInitializeEditor";

export type TiptapEditorProps = {
  borderless?: boolean;
  className?: string;
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  /** Bold, italic and lists only: for text stored as plain text with **bold** and _italic_. */
  isMinimal?: boolean;
  isPending?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  onChangeHTML?: (html: string) => void;
  placeholder?: string;
  tooltipProps?: TooltipProps;
  value?: string;
} & UseEditorOptions;

const TiptapEditorComponent = React.forwardRef(
  (
    {
      borderless,
      className,
      description,
      errorMessage,
      isDisabled,
      isInvalid,
      isMinimal,
      isPending = false,
      isReadOnly = false,
      isRequired,
      label,
      onChange,
      onChangeHTML,
      placeholder,
      tooltipProps,
      value,
      ...editorProps
    }: TiptapEditorProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const editor = useInitializeEditor({
      borderless,
      className,
      isDisabled,
      isMinimal,
      isReadOnly: isReadOnly,
      onChange,
      onChangeHTML,
      placeholder,
      value,
      ...editorProps,
    });

    // Update editor content when value changes for streamed AI responses
    useEffect(() => {
      if (editor && value && (isPending || !editor.getText())) {
        editor.commands.setContent(value);
      }
    }, [editor, value, isPending]);

    if (!editor) {
      return null;
    }

    const focusEditor = () => {
      editor.commands.focus();
    };

    return (
      <div className="flex h-full flex-col">
        <EditorLabel
          focusEditor={focusEditor}
          isRequired={isRequired}
          label={label}
          tooltipProps={tooltipProps}
        />
        <EditorContainer
          borderless={borderless}
          editor={editor}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isMinimal={isMinimal}
          isReadOnly={isReadOnly}
          placeholder={placeholder}
          ref={ref}
        />
        {!isInvalid && description && <Description>{description}</Description>}
        {isInvalid && errorMessage && (
          <span className="pt-sm text-sm text-text-error-primary">
            {errorMessage}
          </span>
        )}
      </div>
    );
  },
);

TiptapEditorComponent.displayName = "TiptapEditor";

export const TiptapEditor = TiptapEditorComponent;
